import type { Column, RawColumn, RelatedColumn } from "./types/column";
import {
  type CountDedupedTraitConfig,
  type CountTraitConfig,
  type FormulaTraitConfig,
  type ListTraitConfig,
  type OrderDedupedTraitConfig,
  type RawSqlTraitConfig,
  type TraitConfig,
  TraitType,
} from "./types/trait-definitions";

// A raw SQL aggregation names its columns through a Handlebars `column`
// helper rather than storing them as structured columns. Handlebars accepts
// either quote style and any surrounding whitespace, so both compile.
const RAW_SQL_COLUMN_PATTERN =
  /\{\{\s*column\s+(?:"([^"]+)"|'([^']+)')\s*\}\}/g;

// A column a trait reads. A bare string is a column name on the model the
// trait aggregates, which is how raw SQL aggregations name their columns.
export type TraitColumnReference = Column | string;

export function rawSqlColumnNames(aggregation: string): string[] {
  return [...aggregation.matchAll(RAW_SQL_COLUMN_PATTERN)]
    .map((match) => match[1] ?? match[2])
    .filter((name): name is string => name !== undefined);
}

// Every column a trait's aggregation reads, as written in its config. The
// fields holding them differ per trait type. Filter conditions sit apart, in
// `config.conditions`.
export function traitColumnReferences(
  type: TraitType,
  config: TraitConfig,
): TraitColumnReference[] {
  switch (type) {
    case TraitType.Count:
    case TraitType.Sum:
    case TraitType.Average: {
      const { column } = config as CountTraitConfig;
      return column ? [column] : [];
    }
    case TraitType.MostFrequent:
    case TraitType.LeastFrequent:
      return [(config as CountDedupedTraitConfig).toSelect];
    case TraitType.First:
    case TraitType.Last: {
      const { orderBy, toSelect } = config as OrderDedupedTraitConfig;
      return [orderBy, toSelect];
    }
    case TraitType.List: {
      const { toSelect, toSelectList, orderBy } = config as ListTraitConfig;
      return [toSelect, ...(toSelectList ?? []), orderBy?.column].filter(
        (column): column is RawColumn | RelatedColumn => column !== undefined,
      );
    }
    case TraitType.RawSql:
      return rawSqlColumnNames((config as RawSqlTraitConfig).aggregation);
    case TraitType.Formula:
      // A formula's inputs are full columns, so they can wrap raw columns,
      // other materialized traits, or inline traits.
      return (config as FormulaTraitConfig).referencedColumns.map(
        ({ column }) => column,
      );
    default:
      // Predictive and tracked audience membership traits configure no columns.
      return [];
  }
}
